/**
 * Platform fee policy shared between the API (authoritative math) and the web
 * (display). All amounts are integer cents; fees round to the nearest cent.
 */

/** Fixed fee per card transferred out to the collector's PSA Vault. */
export const PSA_TRANSFER_FEE_CENTS = 300;

/** Withdrawals under this amount carry the withdrawal fee. */
export const WITHDRAWAL_FEE_THRESHOLD_CENTS = 20_000;

/** Withdrawal fee in basis points of the requested amount. */
export const WITHDRAWAL_FEE_BPS = 350;

/**
 * Fee retained out of a withdrawal request. Zero at or above the threshold.
 * The payout sends the requested amount minus this fee.
 */
export function withdrawalFeeCents(amountCents: number | null | undefined): number | null {
  if (typeof amountCents !== "number" || !Number.isSafeInteger(amountCents) || amountCents <= 0) return null;
  if (amountCents >= WITHDRAWAL_FEE_THRESHOLD_CENTS) return 0;
  return Math.round((amountCents * WITHDRAWAL_FEE_BPS) / 10_000);
}

/** Amount actually paid out for a withdrawal request, net of the fee. */
export function withdrawalNetCents(amountCents: number | null | undefined): number | null {
  const fee = withdrawalFeeCents(amountCents);
  if (fee == null) return null;
  return (amountCents as number) - fee;
}

/**
 * Marketplace seller fees on a collector sale (auction, buy now, accepted
 * offer), deducted from what the seller receives. Buyers pay the price.
 * House (MOT Official) sales carry no fee; instant sellback has its own fee.
 */
export const MARKETPLACE_TRANSACTION_FEE_BPS = 300;
export const MARKETPLACE_TRANSACTION_FEE_FIXED_CENTS = 30;
export const MARKETPLACE_COMMISSION_BPS = 800;
/** Percentage part of the seller fee (3% + 8%), recorded on each sale as fee_bps. */
export const MARKETPLACE_SELLER_FEE_BPS = MARKETPLACE_TRANSACTION_FEE_BPS + MARKETPLACE_COMMISSION_BPS;

export interface MarketplaceSellerFees {
  priceCents: number;
  /** 3% of the price, rounded half-up to the cent, plus $0.30. */
  transactionFeeCents: number;
  /** 8% of the price, rounded half-up to the cent. */
  marketplaceFeeCents: number;
  feeCents: number;
  netCents: number;
}

/** bps of an integer amount, rounded half-up to the cent in integer math. */
function bpsHalfUp(cents: number, bps: number): number {
  return Math.floor((cents * bps + 5_000) / 10_000);
}

/**
 * The one seller-fee calculation, used by settlement and every display.
 * Each component rounds on its own; the total never exceeds the price, so a
 * sale of a few cents pays the seller nothing rather than charging them.
 */
export function marketplaceSellerFees(
  priceCents: number | null | undefined,
  opts: { house?: boolean } = {},
): MarketplaceSellerFees | null {
  if (typeof priceCents !== "number" || !Number.isSafeInteger(priceCents) || priceCents <= 0) return null;
  if (opts.house) return { priceCents, transactionFeeCents: 0, marketplaceFeeCents: 0, feeCents: 0, netCents: priceCents };
  const transactionFeeCents = Math.min(
    priceCents,
    bpsHalfUp(priceCents, MARKETPLACE_TRANSACTION_FEE_BPS) + MARKETPLACE_TRANSACTION_FEE_FIXED_CENTS,
  );
  const marketplaceFeeCents = Math.min(priceCents - transactionFeeCents, bpsHalfUp(priceCents, MARKETPLACE_COMMISSION_BPS));
  const feeCents = transactionFeeCents + marketplaceFeeCents;
  return { priceCents, transactionFeeCents, marketplaceFeeCents, feeCents, netCents: priceCents - feeCents };
}

/** PSA's cut of marketplace revenue under the vault contract. */
export const MARKETPLACE_PSA_FEE_BPS = 350;

/**
 * What the PSA fee is a percentage of (owner, 2026-11-02): the sale price on a
 * house sale, the fee MOT collected on a user-to-user sale. Admin analytics
 * reads this too, so changing a base here changes the dashboard.
 */
export const MARKETPLACE_PSA_FEE_BASE = { house: "sale_price", user: "seller_fee" } as const;

/** PSA fee on one marketplace sale, rounded half-up to the cent. */
export function marketplacePsaFeeCents(sale: { priceCents: number; feeCents: number; house: boolean }): number {
  const base = MARKETPLACE_PSA_FEE_BASE[sale.house ? "house" : "user"];
  return bpsHalfUp(base === "sale_price" ? sale.priceCents : sale.feeCents, MARKETPLACE_PSA_FEE_BPS);
}

/** Total fee the seller pays on a marketplace sale. */
export function marketplaceSellerFeeCents(priceCents: number | null | undefined, opts: { house?: boolean } = {}): number | null {
  return marketplaceSellerFees(priceCents, opts)?.feeCents ?? null;
}

/** What the seller is credited for a marketplace sale, net of fees. */
export function marketplaceSellerNetCents(priceCents: number | null | undefined, opts: { house?: boolean } = {}): number | null {
  return marketplaceSellerFees(priceCents, opts)?.netCents ?? null;
}
